import { parseResult } from '#shared/types';
import { countOf } from '#client/lib/format';

/**
 * The difference between two results for one analyte.
 *
 * Pure, and deliberately small. This file's restraint is its point, and it
 * extends the argument `reference-range.ts` already makes about critical flags:
 * a number a pathologist did not agree to has no business on a patient's report,
 * however easy it would be to compute.
 *
 * ## What this must never compute
 *
 * * **No delta-check limits.** A real LIS delta check is a per-analyte,
 *   per-timeframe threshold a pathologist configures. There is no
 *   `TestParameter.delta_limit` and nobody has set one. Synthesising 10 %, or
 *   20 %, or a fraction of the reference width, puts "significant change" on a
 *   report based on arithmetic nobody signed off.
 * * **No "improving" or "worsening".** Whether up is good depends on the
 *   analyte, and nothing in this schema knows that. Rising haemoglobin and
 *   rising creatinine are not the same news.
 * * **No trend line or extrapolation.** Three irregularly spaced points do not
 *   support a slope, and drawing one implies a prediction.
 * * **No cross-unit or cross-test comparison.** See `deltaBetween`.
 */

type TrendDirection = 'up' | 'down' | 'same';

export interface Delta {
  absolute: number;
  /** Null rather than Infinity when the earlier value is zero. */
  percent: number | null;
  direction: TrendDirection;
}

/**
 * Whether two unit strings describe the same thing.
 *
 * Trimmed and case-insensitive, and nothing more — no conversion table. A lab
 * that moved from mg/dL to mmol/L has results that are not comparable, and
 * silently "converting" them would need a per-analyte molar mass this schema
 * does not have. Refusing is the honest answer.
 */
export function sameUnit(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * The change from `previous` to `current`, or null when there is no honest one.
 *
 * Null when either value is not numeric — `Negative`, `< 0.5` and `A+` are all
 * real results — and null when the units differ. Both go through `parseResult`,
 * the same function `flagFor` uses, so a delta and a flag can never disagree
 * about what a value is.
 */
export function deltaBetween(
  current: { result: string; unit: string },
  previous: { result: string; unit: string },
): Delta | null {
  if (!sameUnit(current.unit, previous.unit)) return null;

  const now = parseResult(current.result);
  const then = parseResult(previous.result);
  if (now === null || then === null) return null;

  const absolute = round(now - then);

  return {
    absolute,
    // Null, not Infinity: a rise from zero has no percentage, and rendering
    // "∞%" on a report is worse than rendering nothing.
    percent: then === 0 ? null : round(((now - then) / Math.abs(then)) * 100),
    // Exactly zero, with **no epsilon**. An epsilon is a clinical significance
    // threshold wearing a disguise — it decides that a change below some size
    // "is not a change", which is precisely the judgement this file refuses to
    // make on a pathologist's behalf.
    direction: now === then ? 'same' : now > then ? 'up' : 'down',
  };
}

const round = (value: number): number => Math.round(value * 10_000) / 10_000;

/**
 * A series for one analyte, oldest first, for the chart.
 *
 * **Oldest first**, unlike every table in this app, and the chart's caption says
 * so: a line read right-to-left is a line nobody reads correctly, and the table
 * beside it is newest-first because that is where the eye should start. Both
 * orders are right for their own job; the mismatch is documented rather than
 * papered over.
 *
 * Non-numeric points are dropped rather than plotted as zero. A `Negative`
 * charted at the origin is a false reading, not a missing one.
 */
export interface TrendPoint {
  report_id: number;
  visited_at: string;
  value: number;
  result: string;
  flag: 'high' | 'low' | null;
}

export function trendSeries(
  cells: readonly ({
    report_id: number;
    visited_at: string;
    result: string;
    flag: 'high' | 'low' | null;
  } | null)[],
): TrendPoint[] {
  return cells
    .flatMap((cell) => {
      if (!cell) return [];
      const value = parseResult(cell.result);
      return value === null
        ? []
        : [
            {
              report_id: cell.report_id,
              visited_at: cell.visited_at,
              value,
              result: cell.result,
              flag: cell.flag,
            },
          ];
    })
    .sort((a, b) => a.visited_at.localeCompare(b.visited_at));
}

/**
 * The chart's accessible description.
 *
 * A `<polyline>` conveys nothing to a screen reader, so the whole series is
 * written out in prose. Not a summary of the trend — the *values*, because a
 * summary would be the "improving/worsening" judgement this file refuses to
 * make.
 */
export function describeSeries({
  name,
  unit,
  points,
  range,
}: {
  name: string;
  unit: string;
  points: readonly TrendPoint[];
  range: string;
}): string {
  if (points.length === 0) return `${name}: no numeric results to chart.`;

  const readings = points
    .map((point) => {
      const day = point.visited_at.slice(0, 10);
      const flag = point.flag ? `, ${point.flag}` : '';
      return `${point.result}${unit ? ` ${unit}` : ''} on ${day}${flag}`;
    })
    .join('; ');

  const reference = range.trim() ? ` Reference range ${range.trim()}.` : '';
  return `${name} over ${countOf(points.length, 'visit')}: ${readings}.${reference}`;
}
